"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { ChevronDown, Crown, ArrowUpRight, Crosshair, Ban, Check, ShieldCheck, X, Target, TrendingUp } from "lucide-react";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { ItemIcon } from "@/components/icons/ItemIcon";
import { SpecIcon } from "@/components/icons/SpecIcon";
import { ProbabilityPill, probabilityColorVar } from "@/components/ProbabilityPill";
import { MelliBar } from "@/components/MelliBar";
import { PageHeader } from "../_components/PageHeader";
import { useLootCache } from "@/lib/loot-cache";
import { useCharState, setRecommendationMode } from "@/lib/state";
import { computeRecommendations, summarizeRecommendations } from "@/lib/engine";
import { CLASS_BY_ID, SPEC_BY_ID } from "@/lib/classes";
import { qualityTextClass, SLOT_LABEL, classColorVar } from "@/lib/wow";
import { cn } from "@/lib/utils";
import type { Recommendation, SkippedItem, OffTargetItem, RecommendationMode } from "@/lib/types";

const SKIP_REASON_LABEL: Record<SkippedItem["reason"], string> = {
  "already-owned": "You already have this",
  "slot-at-myth": "Slot already at Myth",
  "rolled": "Marked rolled",
};

const SKIP_REASON_ICON: Record<SkippedItem["reason"], typeof Ban> = {
  "already-owned": ShieldCheck,
  "slot-at-myth": ShieldCheck,
  "rolled": Check,
};

const OFFTARGET_REASON_LABEL: Record<OffTargetItem["reason"], string> = {
  "not-in-bis": "Not in your BiS",
  "slot-skipped": "Slot marked skip",
};

export default function RecommendationsPage() {
  const state = useCharState();
  const cache = useLootCache();
  const cls = CLASS_BY_ID[state.classID];
  const mode = state.recommendationMode;

  const rows = useMemo(
    () => computeRecommendations({ cache, state }),
    [cache, state],
  );

  // Strict mode reshapes the row list:
  //   - Drop pools where bisHits === 0 (no BiS-preferred upgrades)
  //   - Re-sort by bisHits desc (so pools rich in preferred items
  //     bubble to the top, even if their total `hits` is lower than
  //     pools loaded with any-upgrade fillers)
  // Upgrade mode keeps the engine's original sort.
  const visibleRows = useMemo(() => {
    if (mode !== "strict") return rows;
    return rows
      .filter((r) => r.bisHits > 0)
      .sort((a, b) => {
        const ap = a.bisHits / a.poolSize;
        const bp = b.bisHits / b.poolSize;
        if (ap !== bp) return bp - ap;
        if (a.bisHits !== b.bisHits) return b.bisHits - a.bisHits;
        return (a.pool.label || "").localeCompare(b.pool.label || "");
      });
  }, [rows, mode]);

  const summary = summarizeRecommendations(visibleRows);
  const hasBiS = Object.values(state.bis[state.playedSpecID] ?? {}).some(
    (t) => t && t.mode !== "skip",
  );

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="WHERE TO SPEND"
        title="Recommendations"
        subtitle={summary}
        actions={
          <Link
            href="/bis"
            className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-accent transition-colors"
          >
            <Crosshair className="h-3.5 w-3.5" /> Edit BiS
          </Link>
        }
      />

      <ModeToggle mode={mode} />

      {!hasBiS ? (
        <EmptyBiS />
      ) : visibleRows.length === 0 ? (
        <Card>
          <CardBody className="py-12 text-center">
            <p className="text-sm text-fg-muted">
              {mode === "strict"
                ? "No pool has any BiS-preferred items for your spec."
                : "No pool currently has any BiS-hit eligible items for your spec."}
            </p>
            <p className="text-xs text-fg-subtle mt-2">
              {mode === "strict" && rows.length > 0 && (
                <>
                  {rows.length} {rows.length === 1 ? "pool has" : "pools have"} acceptable
                  upgrades — switch to <strong>Upgrade</strong> mode above to see them.
                </>
              )}
              {mode !== "strict" && (
                <>
                  Try adding more <Link href="/bis" className="text-accent hover:underline">BiS targets</Link>
                  {" "}or check the <Link href="/pools" className="text-accent hover:underline">Pools</Link> page
                  to clear excluded items.
                </>
              )}
            </p>
          </CardBody>
        </Card>
      ) : (
        <ol className="space-y-2">
          {visibleRows.map((r, i) => (
            <RecommendationRow
              key={r.pool.id}
              row={r}
              rank={i + 1}
              isOffSpec={r.specID !== state.playedSpecID}
              classToken={cls?.token}
              mode={mode}
            />
          ))}
        </ol>
      )}

    </div>
  );
}

/** Single row in the hits list. Shared by strict's "preferred" section,
 *  strict's "also acceptable" section, and upgrade-mode's combined list.
 *  `showBadge` controls whether to render a per-item BIS/UPGRADE chip
 *  (only useful in upgrade mode where the list is mixed). `dim` fades
 *  the row for the "also acceptable" subsection. */
function HitRow({
  item,
  isPreferred,
  showBadge,
  dim,
  bossName,
}: {
  item: import("@/lib/types").Item;
  isPreferred: boolean;
  showBadge: boolean;
  dim?: boolean;
  bossName?: string;
}) {
  return (
    <li>
      <Link
        href={`/items/${item.id}`}
        className={cn(
          "px-5 py-2.5 flex items-center gap-3 hover:bg-surface-2/40 transition-colors",
          dim && "opacity-70 hover:opacity-100",
        )}
      >
        <ItemIcon iconUrl={item.iconUrl} iconName={item.iconName} size={32} quality={item.quality} />
        <div className="min-w-0 flex-1">
          <p className={cn("text-sm font-medium truncate", qualityTextClass(item.quality))}>
            {item.name}
          </p>
          <p className="text-[11px] text-fg-subtle font-mono uppercase tracking-wider mt-0.5">
            {SLOT_LABEL[item.slot] ?? item.slot}
            {bossName ? ` · ${bossName}` : ""}
            {item.isToken && " · Tier Token"}
          </p>
        </div>
        {showBadge && (
          isPreferred ? (
            <span
              className={cn(
                "px-1.5 h-5 inline-flex items-center rounded-xs",
                "border border-accent-dim/50 bg-accent/10",
                "text-[10px] font-mono uppercase tracking-wider text-accent",
              )}
              title="In your BiS list (or this slot is catalyzed)"
            >
              BIS
            </span>
          ) : (
            <span
              className={cn(
                "px-1.5 h-5 inline-flex items-center rounded-xs",
                "border border-border bg-surface-2",
                "text-[10px] font-mono uppercase tracking-wider text-fg-subtle",
              )}
              title="Not on your explicit BiS list, but a track upgrade for an unsatisfied slot"
            >
              UPGRADE
            </span>
          )
        )}
        <ArrowUpRight className="h-4 w-4 text-fg-subtle shrink-0 ml-1" />
      </Link>
    </li>
  );
}

function ModeToggle({ mode }: { mode: RecommendationMode }) {
  return (
    <Card>
      <CardBody className="flex items-center justify-between gap-3 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-fg">Recommendation mode</p>
          <p className="text-xs text-fg-muted mt-0.5 truncate">
            {mode === "strict" ? (
              <>
                <strong className="text-fg">Strict BiS</strong> — only listed
                items + catalyzed slots count toward P%.
              </>
            ) : (
              <>
                <strong className="text-fg">Upgrade</strong> — any track
                upgrade in an unsatisfied slot counts.
              </>
            )}
          </p>
        </div>
        <div className="inline-flex items-center gap-0.5 rounded-sm border border-border bg-surface p-0.5 shrink-0">
          <button
            type="button"
            onClick={() => setRecommendationMode("strict")}
            className={cn(
              "h-8 px-3 rounded-xs text-xs font-mono uppercase tracking-wider transition-colors",
              "inline-flex items-center gap-1.5",
              mode === "strict"
                ? "bg-surface-3 text-fg"
                : "text-fg-subtle hover:text-fg hover:bg-surface-2",
            )}
          >
            <Target className="h-3 w-3" />
            Strict BiS
          </button>
          <button
            type="button"
            onClick={() => setRecommendationMode("upgrade")}
            className={cn(
              "h-8 px-3 rounded-xs text-xs font-mono uppercase tracking-wider transition-colors",
              "inline-flex items-center gap-1.5",
              mode === "upgrade"
                ? "bg-surface-3 text-fg"
                : "text-fg-subtle hover:text-fg hover:bg-surface-2",
            )}
          >
            <TrendingUp className="h-3 w-3" />
            Upgrade
          </button>
        </div>
      </CardBody>
    </Card>
  );
}

function RecommendationRow({
  row,
  rank,
  isOffSpec,
  classToken,
  mode,
}: {
  row: Recommendation;
  rank: number;
  isOffSpec: boolean;
  classToken?: string;
  mode: RecommendationMode;
}) {
  const [expanded, setExpanded] = useState(rank === 1);

  // Mode-aware partitioning: in strict mode the headline + bar reflect
  // BiS-preferred only; the acceptable upgrades are demoted to a
  // separate section. In upgrade mode everything stacks together.
  const preferredHits = row.hitItems.filter((h) => h.isPreferred);
  const acceptableHits = row.hitItems.filter((h) => !h.isPreferred);
  const displayHits = mode === "strict" ? row.bisHits : row.hits;
  const displayP = row.poolSize > 0 ? displayHits / row.poolSize : 0;
  return (
    <li>
      <Card className="overflow-hidden">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full px-5 py-4 text-left hover:bg-surface-2/40 transition-colors"
        >
          <div className="flex items-center gap-4">
            <span className="font-mono tabular text-xs text-fg-subtle w-6 text-center">
              {String(rank).padStart(2, "0")}
            </span>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-base font-medium text-fg truncate">
                  {row.pool.label}
                </h3>
                {row.pool.isRaid ? (
                  <span className="eyebrow inline-flex items-center gap-1 text-quality-epic">
                    <Crown className="h-3 w-3" /> Raid · {row.pool.instanceName}
                  </span>
                ) : (
                  <span className="eyebrow text-quality-rare">
                    Mythic+ Dungeon
                  </span>
                )}
              </div>
              <div className="mt-1.5 flex items-center gap-2.5 flex-wrap">
                <SpecIcon specID={row.specID} size={20} />
                <span className="text-xs text-fg-muted">
                  Loot spec:{" "}
                  <span style={{ color: classToken ? `var(--color-class-${classToken})` : undefined }}>
                    {row.specName}
                  </span>
                </span>
                {isOffSpec && (
                  <span className="eyebrow text-warning">SWITCH LOOT SPEC</span>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <ProbabilityPill p={displayP} hits={displayHits} poolSize={row.poolSize} />
              <div className="w-[120px]">
                <MelliBar value={displayP} color={probabilityColorVar(displayP)} size="sm" />
              </div>
              {mode === "strict" && acceptableHits.length > 0 && (
                <span className="text-[10px] text-fg-subtle font-mono uppercase tracking-wider">
                  +{acceptableHits.length} acceptable
                </span>
              )}
            </div>

            <ChevronDown
              className={cn(
                "h-4 w-4 text-fg-subtle ml-1 transition-transform",
                expanded && "rotate-180",
              )}
            />
          </div>
        </button>

        {expanded && (
          <CardBody className="border-t border-border bg-surface-2/30 p-0">
            {/* Hits — meaning of "hits" depends on mode:
                - strict: BiS-preferred only (counted toward P%)
                - upgrade: everything (BIS / UPGRADE badges per item) */}
            <div className="flex items-center justify-between px-5 pt-3">
              <p className="eyebrow">
                {mode === "strict" ? "Preferred BiS" : "Hits this pool"} · {displayHits}
              </p>
              <p className="text-[10px] text-fg-subtle font-mono uppercase tracking-wider">
                Probability {(displayP * 100).toFixed(0)}% · {displayHits}/{row.poolSize}
              </p>
            </div>
            <ul className="divide-y divide-border">
              {(mode === "strict" ? preferredHits : row.hitItems).map(({ item, isPreferred }) => (
                <HitRow
                  key={item.id}
                  item={item}
                  isPreferred={isPreferred}
                  showBadge={mode === "upgrade"}
                  bossName={!row.pool.isRaid ? item.bossName : undefined}
                />
              ))}
            </ul>

            {/* Strict mode: any-upgrade items are demoted to a separate
                section ("Also acceptable") that's still informative but
                doesn't drive the recommendation math. */}
            {mode === "strict" && acceptableHits.length > 0 && (
              <>
                <div className="flex items-center justify-between px-5 pt-4 pb-1 border-t border-border">
                  <p className="eyebrow text-fg-subtle">
                    Also acceptable · {acceptableHits.length}
                  </p>
                  <p className="text-[10px] text-fg-subtle font-mono uppercase tracking-wider">
                    Track upgrade, not on your BiS list
                  </p>
                </div>
                <ul className="divide-y divide-border">
                  {acceptableHits.map(({ item }) => (
                    <HitRow
                      key={`acc-${item.id}`}
                      item={item}
                      isPreferred={false}
                      showBadge={false}
                      dim
                      bossName={!row.pool.isRaid ? item.bossName : undefined}
                    />
                  ))}
                </ul>
              </>
            )}

            {/* Off-target: items in the pool the user could roll into
                but doesn't want (not on their BiS list). Bonus rolls
                landing here are wasted just like the auto-skipped ones —
                surfacing them lets the user see the FULL "wasted-roll"
                surface area, not just BiS-matching skips. */}
            {row.offTargetItems.length > 0 && (
              <>
                <div className="flex items-center justify-between px-5 pt-4 pb-1 border-t border-border">
                  <p className="eyebrow text-fg-subtle">
                    Off-target · {row.offTargetItems.length}
                  </p>
                  <p className="text-[10px] text-fg-subtle font-mono uppercase tracking-wider">
                    Wasted roll if landed
                  </p>
                </div>
                <ul className="divide-y divide-border">
                  {row.offTargetItems.map((o) => (
                    <li key={`offtarget-${o.item.id}`}>
                      <Link
                        href={`/items/${o.item.id}`}
                        className="px-5 py-2.5 flex items-center gap-3 hover:bg-surface-2/40 transition-colors opacity-50 hover:opacity-80"
                      >
                        <ItemIcon
                          iconUrl={o.item.iconUrl}
                          iconName={o.item.iconName}
                          size={32}
                          quality={o.item.quality}
                        />
                        <div className="min-w-0 flex-1">
                          <p className={cn(
                            "text-sm font-medium truncate line-through decoration-fg-subtle/40",
                            qualityTextClass(o.item.quality),
                          )}>
                            {o.item.name}
                          </p>
                          <p className="text-[11px] text-fg-subtle font-mono uppercase tracking-wider mt-0.5">
                            {SLOT_LABEL[o.item.slot] ?? o.item.slot}
                            {o.item.bossName && row.pool.isRaid === false ? ` · ${o.item.bossName}` : ""}
                            {o.item.isToken && " · Tier Token"}
                          </p>
                        </div>
                        <span className="inline-flex items-center gap-1 shrink-0 text-[10px] font-mono uppercase tracking-wider text-fg-subtle">
                          <X className="h-3 w-3" />
                          {OFFTARGET_REASON_LABEL[o.reason]}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/* Skipped items: BiS-matching loot the engine took out
                of the hit count (already owned, slot at Myth, rolled).
                Lets the user see what their bonus roll WON'T improve
                without diving into the loot table. */}
            {row.skippedItems.length > 0 && (
              <>
                <div className="flex items-center justify-between px-5 pt-4 pb-1 border-t border-border">
                  <p className="eyebrow text-fg-subtle">
                    Auto-skipped · {row.skippedItems.length}
                  </p>
                  <p className="text-[10px] text-fg-subtle font-mono uppercase tracking-wider">
                    Bonus roll won&apos;t upgrade these
                  </p>
                </div>
                <ul className="divide-y divide-border">
                  {row.skippedItems.map((s) => {
                    const Icon = SKIP_REASON_ICON[s.reason];
                    return (
                      <li key={`${s.reason}-${s.item.id}`}>
                        <Link
                          href={`/items/${s.item.id}`}
                          className="px-5 py-2.5 flex items-center gap-3 hover:bg-surface-2/40 transition-colors opacity-60 hover:opacity-90"
                        >
                          <ItemIcon
                            iconUrl={s.item.iconUrl}
                            iconName={s.item.iconName}
                            size={32}
                            quality={s.item.quality}
                          />
                          <div className="min-w-0 flex-1">
                            <p className={cn(
                              "text-sm font-medium truncate line-through decoration-fg-subtle/60",
                              qualityTextClass(s.item.quality),
                            )}>
                              {s.item.name}
                            </p>
                            <p className="text-[11px] text-fg-subtle font-mono uppercase tracking-wider mt-0.5">
                              {SLOT_LABEL[s.item.slot] ?? s.item.slot}
                              {s.item.bossName && row.pool.isRaid === false ? ` · ${s.item.bossName}` : ""}
                              {s.item.isToken && " · Tier Token"}
                            </p>
                          </div>
                          <span className={cn(
                            "inline-flex items-center gap-1 shrink-0",
                            "text-[10px] font-mono uppercase tracking-wider text-fg-subtle",
                          )}>
                            <Icon className="h-3 w-3" />
                            {SKIP_REASON_LABEL[s.reason]}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </CardBody>
        )}
      </Card>
    </li>
  );
}

function EmptyBiS() {
  return (
    <Card>
      <CardBody className="py-12 text-center">
        <p className="font-display text-lg text-fg">No BiS targets configured.</p>
        <p className="mt-2 text-sm text-fg-muted max-w-md mx-auto">
          For each of your 14 slots, mark whether you want{" "}
          <span className="text-fg">any catalyzable piece</span>,{" "}
          <span className="text-fg">specific items only</span>, or to{" "}
          <span className="text-fg">skip</span> the slot.
        </p>
        <Link
          href="/bis"
          className="inline-flex items-center gap-2 mt-6 px-5 h-10 rounded-sm bg-accent text-bg font-medium hover:bg-accent-bright transition-colors text-sm"
        >
          Configure BiS <ArrowUpRight className="h-4 w-4" />
        </Link>
      </CardBody>
    </Card>
  );
}
